<script setup lang="ts">
import type { BrandType, GenericRateType } from '~/types'
import PhoneIcon from '@heroicons/vue/20/solid/PhoneIcon'
import Button from '~/components/UI/Button.vue'

const props = defineProps<{ data: GenericRateType }>()

const { getBrandById } = useBrandStore()
const { setSelectedRate } = useRateStore()

const brand = computed<BrandType | undefined>(() =>
  getBrandById(props.data?.brandId)
)

// Se il nome dell'offerta contiene già il nome del brand (es. "Fastweb Casa Light"),
// il badge del logo è puramente decorativo: l'alt duplicherebbe il testo visibile
// adiacente (WAVE "Redundant alternative text")
const brandLogoAlt = computed(() => {
  const brandName = brand.value?.name
  if (!brandName) return ''
  return props.data.name?.toLowerCase().includes(brandName.toLowerCase()) ? '' : brandName
})

const detailUrl = computed(() => `/categoria/${props.data.category}/${props.data.slug || props.data.id}`)
</script>

<template>
  <div class="h-full w-full max-w-[360px]">
    <div class="relative overflow-hidden flex flex-col w-full px-8 justify-center items-center">
      <!-- Area immagine puramente decorativa: nessun link, l'unico link della card è "Scopri di più" sotto -->
      <div class="relative w-full bg-slate-700 flex justify-start items-center py-6 px-4 rounded-t-[30px]">
        <div class="w-full pr-28 h-10 flex items-center">
          <span class="text-xs sm:text-sm text-white font-bold line-clamp-2 uppercase leading-tight">
            {{ data.name }}
          </span>
        </div>
        <div
          class="absolute -bottom-5 right-5 sm:-right-5 w-[120px] h-[48px] bg-white p-1 ring-4 ring-zinc-300/25 rounded-ss-[48px] rounded-ee-[48px]">
          <NuxtImg v-if="brand" class="h-full w-full rounded-ss-[48px] rounded-ee-[48px] object-contain"
            :src="(brand.assetUrl)" :alt="brandLogoAlt" width="120" height="48" />
        </div>
      </div>
      <div class="bg-white h-40 w-full flex justify-center items-center">
        <NuxtImg v-if="data.assetUrl" class="h-full w-full object-cover max-w-[360px]" :src="data.assetUrl"
          alt="" width="360" height="160" />
        <NuxtImg v-else-if="data.fallbackAssetUrl" class="h-full w-full max-w-[360px] object-contain"
          :src="data.fallbackAssetUrl" alt="" width="360" height="160" />
      </div>
      <div
        class="relative w-full bg-slate-900 flex flex-col justify-center items-center gap-2 pt-6 pb-4 px-4 rounded-b-[30px]">
        <NuxtLink :to="detailUrl" :aria-label="`Vedi i dettagli dell'offerta ${data.name}`">
          <Button class="absolute -top-4 left-1/2 transform -translate-x-1/2 w-[140px]" variant="primary" size="medium"
            gradient>
            <div class="flex justify-center items-center text-center text-xs lg:text-sm">
              Scopri di più
            </div>
          </Button>
        </NuxtLink>
        <div class="w-full flex items-center scale-75 opacity-50">
          <div class="border-t border-1 border-white flex-1"></div>
          <div class="px-3 text-white font-bold text-xs">OPPURE</div>
          <div class="border-t border-1 border-white flex-1"></div>
        </div>
        <div class="flex flex-col gap-2 w-full">
          <Button variant="neutral" size="medium" outlined prevent-default stop-propagation
            @click="setSelectedRate(data)">
            <div class="flex justify-center items-center gap-1 text-xs lg:text-sm">
              <PhoneIcon class="h-4 w-4" aria-hidden="true" />
              Ti chiamiamo noi
            </div>
          </Button>
         <a 
  href="https://wa.me/393509925946?text=Ciao!%20Sono%20interessato%20alle%20offerte%20del%20sito%20RisparmiaTu.com!"
  target="_blank"
  rel="noopener noreferrer"
  class="inline-block"
  @click.stop
>
  <Button variant="primary" size="medium" class="w-full bg-gradient-to-r from-green-700 to-green-800 hover:from-green-800 hover:to-green-900 text-white border-0">
    <div class="flex justify-center items-center gap-2 text-xs lg:text-sm">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
      </svg>
      Chatta con noi
    </div>
  </Button>
</a>
        </div>
      </div>
    </div>
  </div>
</template>